// Device Detection Log Component
// Displays real-time logs for USB device detection and audio troubleshooting

import React, { useEffect, useRef } from 'react';

const DeviceDetectionLog = ({ logs, textColor, maxHeight = 200 }) => {
    const logEndRef = useRef(null);
    const logContainerRef = useRef(null);

    // Auto-scroll to bottom when new logs arrive
    useEffect(() => {
        if (logEndRef.current && logContainerRef.current) {
            // Only auto-scroll if we're already near the bottom
            const container = logContainerRef.current;
            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
            
            if (isNearBottom || logs.length === 1) {
                // Use optional chaining to handle testing environments where scrollIntoView may not be available
                logEndRef.current.scrollIntoView?.({ behavior: 'smooth' });
            }
        }
    }, [logs]);

    if (!logs || logs.length === 0) {
        return null;
    }

    const getLogStyle = (log) => {
        // Apply different styles based on log content
        if (log.includes('✓')) {
            return { color: '#44ff44' }; // Success - green
        } else if (log.includes('⚠️') || log.includes('WARNING')) {
            return { color: '#ffaa00' }; // Warning - orange
        } else if (log.includes('ERROR') || log.includes('❌')) {
            return { color: '#ff4444' }; // Error - red
        } else if (log.includes('USB') || log.includes('External')) {
            return { color: '#88ccff' }; // USB/External - light blue
        } else if (log.includes('Built-in') || log.includes('Internal')) {
            return { color: '#cccccc' }; // Built-in - gray
        } else if (log.includes('💡')) {
            return { color: '#ffff44' }; // Info/Tip - yellow
        }
        return { color: textColor };
    };

    return (
        <div style={{
            marginTop: '10px',
            padding: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '4px',
            border: `1px solid ${textColor}20`
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '5px'
            }}>
                <div style={{
                    fontSize: '12px',
                    color: textColor,
                    opacity: 0.8,
                    fontWeight: 'bold'
                }}>
                    🔍 Device Detection Log
                </div>
                <div style={{
                    fontSize: '10px',
                    color: textColor,
                    opacity: 0.6
                }}>
                    {logs.length} entries
                </div>
            </div>
            <div 
                ref={logContainerRef}
                style={{
                    maxHeight: `${maxHeight}px`,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '3px',
                    padding: '8px',
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    lineHeight: '1.4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                }}
            >
                {logs.map((log, index) => (
                    <div 
                        key={index} 
                        style={{
                            marginBottom: '2px',
                            ...getLogStyle(log)
                        }}
                    >
                        {log}
                    </div>
                ))}
                <div ref={logEndRef} />
            </div>
            <div style={{
                marginTop: '5px',
                fontSize: '9px',
                color: textColor,
                opacity: 0.5,
                textAlign: 'center'
            }}>
                Scroll to view all logs
            </div>
        </div>
    );
};

export default DeviceDetectionLog;
